import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Switch, Route, useRouteMatch, matchPath, useLocation, useParams } from 'react-router-dom';
import { throttle } from 'underscore';

import { GetDefaultMainNavItems } from '../../../../../store/utils/create-event';
import { ENavbarTypes, NavMainItems, NavProfileItems } from '../../../../../types/working-model';
import { ICONS } from '../../../../general-ui/icon';
import MarketingNavV2 from '../../../../live-event/marketing-page/navigation-v2/marketing-nav-v2';
import People from './people';
import Home from './home';
import MainPreview, { PostRegisterSideBar } from './main-preview';
import SessionItemsList from './session-items-list';
import SessionView from '../../../../live-event/session/session';
import { useTypedSelector } from '../../../../../store/reducers/use-typed-selector';
import CustomPageEditor from '../editor/custom-page/custom-page';
import { EditorSizes } from '../../../../../types/template-layouts';
import PreviewEventChatMain from '../../../../live-event/event-chat/preview-event-chat-main';
import EventEditorHeader from '../editor/event-editor-header';
import { PATHNAMES } from '../../../../../utils/admin-routing-utils';
import { OptionalComponent } from '../../../../../utils/optional-component';
import { DisplayEmail } from '../../scheduled-emails/display-email';
import { useReadonlyMode } from '../../shared/use-readonly-mode';
import { getTemplateClassName, isRtlLanguage } from '../../../../../utils/utils';
import { ParamsProps } from '../../../../live-event/live-event';
import CookieBannerStickyWrapper from '../../../../general-ui/cookie-banner/cookie-banner-sticky-wrapper';
import CookieBanner from '../../../../general-ui/cookie-banner/cookie-banner';
import { useIsNewNavigation } from '../../../../../hooks/navigation.hooks';
import { useIsSingleSessionWithoutHome } from '../../../../../hooks/session.hooks';

// import './post-regist-preview.scss';
//If working on Sessions tab of Main navigation, this file contains some old styling for the editor sessions view that may be helpful, but importing this fully messes up the editor window. Please find a way to separate out the necessary styling into a separate file, or go through and make sure to remove anything effecting the editor window sizing and position/margin

export const mainNavIcons = (name: NavMainItems): string | null => {
	switch (name) {
		case NavMainItems.Home: return ICONS.HOME;
		case NavMainItems.Sessions: return ICONS.CALENDAR;
		case NavMainItems.People: return ICONS.PEOPLE_NAV;
		case NavMainItems.Session: return ICONS.CALENDAR;
		case NavMainItems.Leaderboard: return ICONS.LEADERBOARD_NAV;
	}
	return null;
};

export const profileNavIcons = (name: NavProfileItems): string | null => {
	if (name === NavProfileItems.Profile) return ICONS.PERSON;
	return null;
};

const PostRegisterPreview: React.FC = () => {
	const [chatTopPosition, setChatTopPosition] = useState(0);
	const [cookieBannerTopPosition, setCookieBannerTopPosition] = useState(0);
	const [cookieBannerHeight, setCookieBannerHeight] = useState(0);
	const [settingPosition, setSettingPosition] = useState(true);
	const [editorSizeChangedAsNum, setEditorSizeChangedAsNum] = useState(0);

	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);
	const panelOpen = useTypedSelector(state => state.EventPanelReducer.panelOpen);
	const logo = useTypedSelector(state => state.CreateEventReducer?.workingEvent?.homepage?.header?.logo);
	const _template = useTypedSelector(state => state.CreateEventReducer.workingEvent?.template.name);
	const currentPage = useTypedSelector(state => state.CreateEventReducer.currentPostRegisterPage);
	const eventChatEnabled = useTypedSelector(state => state.CreateEventReducer.workingEvent?.social_settings?.messaging);
	const loadingEvent = useTypedSelector(state => state.CreateEventReducer.loadingEvent);
	const blurEditor = useTypedSelector(state => state.CreateEventReducer.blurEditor);
	const loadingEmails = useTypedSelector(state => !!state.CreateEventReducer.scheduledEmails?.loadingEmails);

	// Gets forwarded to child homepage component for auto scrolling on module clicks
	const previewContainerRef = useRef<HTMLDivElement | null>(null);

	const location = useLocation();
	const isNewNavigation = useIsNewNavigation();

	// because of absolute positioning of chat bubble it won't scroll within the editor
	// we need to dynamically set the scroll top position to keep it moving with scroll
	const throttleScroll = throttle((event: any) => {
		if (eventChatEnabled) {
			setChatTopPosition(event?.target?.scrollTop);
		}
		setCookieBannerTopPosition(event?.target?.scrollTop);
	}, 1000);

	const isSingleSession = useMemo(() => workingEvent?.sessions.length === 1, [workingEvent]);
	const hasHomepage = workingEvent?.homepage;
	const match = useRouteMatch();
	const { language }: ParamsProps = useParams();

	const eventMainNavItems = workingEvent?.homepage?.event_main_nav_items || GetDefaultMainNavItems();

	const activeCustomPages = workingEvent?.custom_pages?.filter(page => page.is_on);

	const template = workingEvent?.template?.name?.toLocaleLowerCase().replace(/\s/g, '') ?? '';

	const [isEmail, isSession] = useMemo(() => {
		const excludeChat = matchPath(location.pathname, {
			path: [
				PATHNAMES.Event.EmailList,
				PATHNAMES.Event.EmailDetails
			],
			exact: true
		});

		const isSession = !matchPath(location.pathname, {
			path: [
				PATHNAMES.Event.HomePath,
				PATHNAMES.Event.PeoplePath,
				PATHNAMES.Event.SessionsPath,
				PATHNAMES.Event.CustomPage,
				PATHNAMES.Event.EmailList,
				PATHNAMES.Event.EmailDetails
			]
		});

		return [excludeChat, isSession && isSingleSession];
	}, [location.pathname, isSingleSession]);

	const isReadOnly = useReadonlyMode(isSession) && !isEmail;
	const isSingleSessionWithoutHome = useIsSingleSessionWithoutHome();
	const navbarType = workingEvent?.settings?.event_navbar?.navbarType;
	const isTopNavbar = navbarType === ENavbarTypes.Horizontal;
	const isDesktop = editorSize === EditorSizes.desktop;

	// when we change the editor size, we need to force a re-render of the cookie banner
	// but we must wait a second because it takes some time for the editor to resize
	useEffect(() => {
		setTimeout(() => {
			setEditorSizeChangedAsNum(editorSize === EditorSizes.desktop
				? 0
				: editorSize === EditorSizes.tablet
					? 1
					: 2
			);
		}, 1000);
	}, [editorSize]);

	useEffect(() => {
		// on page load force a 1px scroll so that we can trigger the "onScroll" event to get the correct heights
		const containerEl = previewContainerRef.current;
		if (containerEl) {
			containerEl.scrollTop = 1;
			// timeout necessary or else the scroll event won't fire
			setTimeout(() => {
				containerEl.scrollTop = 0;
				setSettingPosition(false);
			}, 50);
		}
	}, [previewContainerRef]);

	if (!workingEvent) return null;
	return (
		<div
			className={classNames("post-register-preview editor-main-border", { "force-rtl": isRtlLanguage(language) })}
		>
			<EventEditorHeader workingEvent={workingEvent} />
			<div
				ref={previewContainerRef}
				className={classNames(
					"post-register-preview-container editor editor-main-container",
					editorSize,
					template,
					{
						"panel-open": !!panelOpen,
						"live-event-container": !currentPage,
						"session-preview-wrapper": isSingleSession && editorSize !== EditorSizes.desktop,
						"loading": loadingEvent && blurEditor,
						"email-preview": [PATHNAMES.Event.EmailDetails, PATHNAMES.Event.EmailList].includes(match.path),
						'read-only': isReadOnly,
						'new-top-navigation': isDesktop && (isTopNavbar && isNewNavigation || isSingleSessionWithoutHome && isNewNavigation),
					})
				}
				onScroll={throttleScroll}
			>
				<OptionalComponent display={isDesktop && isNewNavigation && isSingleSessionWithoutHome}>
					<MarketingNavV2 template={template} previewHide={false} />
				</OptionalComponent>
				<Switch>
					<Route path={PATHNAMES.Event.HomePath}>
						<Home
							navItem={eventMainNavItems.find(item => item.name === NavMainItems.Home)}
							eventMainNavItems={eventMainNavItems}
							ref={previewContainerRef}
						/>
					</Route>
					<Route path={PATHNAMES.Event.PeoplePath}>
						<People navItem={eventMainNavItems.find(item => item.name === NavMainItems.People)} />
					</Route>
					<Route path={PATHNAMES.Event.SessionsPath}>
						<SessionItemsList eventMainNavItems={eventMainNavItems} />
					</Route>
					<Route exact path={PATHNAMES.Event.CustomPage}>
						<CustomPageEditor ref={previewContainerRef} />
					</Route>
					<Route exact path={[PATHNAMES.Event.EmailList, PATHNAMES.Event.EmailDetails]}>
						{/* display null if loading emails so we can "re-initialize" quill with new content if admin updates the email schedule */}
						<OptionalComponent display={!loadingEmails}>
							<DisplayEmail />
						</OptionalComponent>
					</Route>
					<Route>
						{isSingleSession ? (
							<>
								{hasHomepage && (
									<PostRegisterSideBar
										isSingleSession
										disableProfileBubble
										{...{ eventMainNavItems, template: getTemplateClassName(_template), logo }}
										activeMenuItem={NavMainItems.Session}
										customPages={activeCustomPages}
									/>
								)}
								<SessionView singleSession previewBundle={workingEvent} />
							</>
						) : (
							<MainPreview eventMainNavItems={eventMainNavItems} />
						)}
					</Route>
				</Switch>

				<OptionalComponent display={!isEmail}>
					<PreviewEventChatMain chatTopPosition={chatTopPosition} />
				</OptionalComponent>

				<CookieBannerStickyWrapper
					style={{
						// position sticky breaks on the editor event page so we must use absolute positioning and calculate the top position
						// luckily we can use the existing scroll event to calculate the top position
						position: 'absolute',
						top: `${(previewContainerRef.current?.offsetHeight || 0) + cookieBannerTopPosition - (cookieBannerHeight + 50)}px`,
						opacity: settingPosition ? 0 : 1, // prevents the cookie banner from flashing on the screen
					}}
				>
					<CookieBanner
						isEditor
						forceShowBanner={!!workingEvent?.settings.gdpr?.enable_cookie_banner}
						containerHeight={height => {
							setCookieBannerHeight(height);
						}}
						forceHeightRecalcutation={cookieBannerTopPosition + editorSizeChangedAsNum}
					/>
				</CookieBannerStickyWrapper>
			</div>
		</div>
	);
};

export default PostRegisterPreview;
